"use client";

import { useEffect, useState } from "react";
import {
  useSessionWhoami,
  type SessionWhoamiResult,
} from "~/components/useSessionWhoami";
import { hasAuthHintCookie, syncAuthHintDocumentClass } from "~/utils/authHint";
import { clerkAwareLogoutClick } from "~/utils/clerkBrowserSignOut";

// The black member button in the public headers. globals.css shows its
// "Sign in" or "Dashboard" link from html.twt-auth-present, and its menu
// offers Sign in, or Dashboard and Sign out. Both read the one signedIn value
// below, so the label and the menu can never disagree.

export type MemberAccess = {
  signedIn: boolean;
  rememberedSession: SessionWhoamiResult["remembered"];
  signInLabel: string;
};

export function memberAccessFor(
  signedIn: boolean,
  session: SessionWhoamiResult
): MemberAccess {
  const rememberedSession = signedIn ? null : session.remembered;
  return {
    signedIn,
    rememberedSession,
    signInLabel: rememberedSession
      ? `Sign in as ${rememberedSession.label}`
      : "Sign in",
  };
}

export function useMemberAccess(): MemberAccess {
  const session = useSessionWhoami();
  // The latest answer wins. The auth hint cookie answers on mount and when
  // the tab regains focus or comes back from the back-forward cache, because
  // a sign-in or sign-out in another tab changes it at once. Every answer
  // from /api/session/whoami then overrides it, and that response also
  // repairs the cookie. null until mounted, so the class the prepaint script
  // set stays untouched until there is an answer.
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    const readCookie = () => setSignedIn(hasAuthHintCookie());
    readCookie();
    window.addEventListener("focus", readCookie);
    window.addEventListener("pageshow", readCookie);
    return () => {
      window.removeEventListener("focus", readCookie);
      window.removeEventListener("pageshow", readCookie);
    };
  }, []);

  // Keyed on the session object, which every resolved refresh replaces, so a
  // repeated "signed out" answer still corrects a cookie read in between.
  useEffect(() => {
    if (session.status !== "resolved") return;
    setSignedIn(Boolean(session.active));
  }, [session]);

  useEffect(() => {
    if (signedIn === null) return;
    syncAuthHintDocumentClass(signedIn);
  }, [signedIn]);

  return memberAccessFor(Boolean(signedIn), session);
}

// The account rows at the bottom of the member button's menu.
export function MemberAccessMenuItems({
  access,
  memberHref = "/dashboard",
  memberLabel = "Dashboard",
  itemClassName,
  onNavigate,
}: {
  access: MemberAccess;
  memberHref?: string;
  memberLabel?: string;
  itemClassName: string;
  onNavigate?: () => void;
}) {
  if (!access.signedIn) {
    return (
      <a
        href={memberHref}
        role="menuitem"
        className={itemClassName}
        onClick={onNavigate}
      >
        {access.signInLabel}
      </a>
    );
  }

  return (
    <>
      <a
        href={memberHref}
        role="menuitem"
        className={itemClassName}
        onClick={onNavigate}
      >
        {memberLabel}
      </a>
      {/* A full page load, so /api/logout can clear the session cookies and
          return the member to the homepage signed out. */}
      <a
        href="/api/logout"
        role="menuitem"
        className={itemClassName}
        onClick={clerkAwareLogoutClick}
      >
        Sign out
      </a>
    </>
  );
}
